import { Button, Flex, Form, Input, Select, type FormInstance } from "antd";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import SelectDropdownIcon from "../../../../assets/icons/SelectDropdownIcon";
import FileUpload from "../../../../components/FileUpload";
import { selectSearchOption } from "../../../../utils/string.utils";
import AddIcon from "../_icons/AddIcon";

type Props = {
  form: FormInstance<any>;
};

export default function EducationInfo({ form }: Props) {
  return (
    <>
      <Form.List initialValue={[{}]} name="education">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <div
                key={key}
                className="p-4 rounded-lg border border-theme-zinc-200 mb-3"
              >
                {fields.length > 1 && (
                  <Flex justify="end">
                    <Button
                      icon={<DeleteIcon className="text-theme-red-500" />}
                      onClick={() => remove(name)}
                      size="small"
                      type="dashed"
                      className="!size-7"
                    />
                  </Flex>
                )}
                <div className="grid md:grid-cols-2 gap-5">
                  <Form.Item
                    {...restField}
                    name={[name, "level"]}
                    label="ডিগ্রী/সার্টিফিকেট"
                    rules={[
                      {
                        required: true,
                        message: "ডিগ্রী/সার্টিফিকেট নির্বাচন করুন",
                      },
                    ]}
                  >
                    <Select
                      placeholder="নির্বাচন করুন"
                      size="large"
                      allowClear
                      showSearch
                      filterOption={selectSearchOption}
                      options={[
                        { label: "SSC", value: "SSC" },
                        { label: "HSC", value: "HSC" },
                        { label: "Bachelor", value: "Bachelor" },
                        { label: "Masters", value: "Masters" },
                      ]}
                      suffixIcon={<SelectDropdownIcon />}
                    />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, "group"]}
                    label="গ্রুপ/বিষয়"
                    rules={[
                      {
                        required: false,
                        message: "গ্রুপ/বিষয় দিন",
                      },
                    ]}
                  >
                    <Input size="large" placeholder="গ্রুপ/বিষয়" />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, "institute"]}
                    label="প্রতিষ্ঠান"
                    rules={[
                      {
                        required: false,
                        message: "প্রতিষ্ঠানের নাম দিন",
                      },
                    ]}
                  >
                    <Input size="large" placeholder="প্রতিষ্ঠানের নাম" />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, "passingYear"]}
                    label="পাসের সন"
                    rules={[
                      {
                        required: false,
                        message: "পাসের সন দিন",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.resolve();
                          }
                          const num = Number(value);
                          if (num < 0) {
                            return Promise.reject(
                              new Error("পাসের সন নেগেটিভ ভ্যালু দেওয়া যাবে না")
                            );
                          }
                          if (!Number.isInteger(num)) {
                            return Promise.reject(
                              new Error("পাসের সন দশমিকে দেওয়া যাবে না")
                            );
                          }
                          if (num < 1900) {
                            return Promise.reject(
                              new Error("পাসের সন ১৯০০ এর কম হতে পারবে না")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input type="number" size="large" placeholder="পাসের সন" />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, "result"]}
                    label="ফলাফল"
                    rules={[
                      {
                        required: false,
                        message: "ফলাফল দিন",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.resolve();
                          }
                          const num = Number(value);
                          const education =
                            form.getFieldValue("education") || [];
                          const currentLevel = education[name]?.level;

                          if (isNaN(num)) {
                            return Promise.reject(
                              new Error("ফলাফল অবশ্যই নম্বর হতে হবে")
                            );
                          }
                          if (num < 0) {
                            return Promise.reject(
                              new Error("ফলাফল নেগেটিভ হতে পারবে না")
                            );
                          }
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "ফলাফল দশমিকের পর ২ সংখ্যার বেশি হতে পারবে না"
                              )
                            );
                          }

                          if (
                            currentLevel === "Bachelor" ||
                            currentLevel === "Masters"
                          ) {
                            if (num > 4) {
                              return Promise.reject(
                                new Error(
                                  `${
                                    currentLevel === "Bachelor"
                                      ? "ব্যাচেলর"
                                      : "মাস্টার্স"
                                  } এর ফলাফল ৪ এর বেশি হতে পারবে না`
                                )
                              );
                            }
                          }

                          if (
                            currentLevel === "SSC" ||
                            currentLevel === "HSC"
                          ) {
                            if (num > 5) {
                              return Promise.reject(
                                new Error(
                                  `${currentLevel} এর ফলাফল ৫ এর বেশি হতে পারবে না`
                                )
                              );
                            }
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                    dependencies={[["education", name, "level"]]}
                  >
                    <Input size="large" placeholder="জি.পি.এ / বিভাগ" />
                  </Form.Item>
                </div>

                <Form.Item
                  {...restField}
                  // name={[name, "certificate"]} // TODO: Add certificate upload
                  label="সার্টিফিকেট আপলোড করুন"
                  className="mb-4"
                  valuePropName="fileUrl"
                >
                  <FileUpload
                    form={form}
                    accept=".png, .jpg, .jpeg"
                    name="certificate"
                    onChange={(url) =>
                      form.setFieldsValue({
                        education: form
                          .getFieldValue("education")
                          .map((item: any, idx: number) => {
                            if (idx === name)
                              return { ...item, certificate: url };
                            return item;
                          }),
                      })
                    }
                  />
                </Form.Item>
              </div>
            ))}

            <Button
              size="small"
              type="text"
              icon={<AddIcon />}
              onClick={() => add()}
              className="!bg-transparent"
            >
              <span className="text-theme-blue-600 text-lg font-medium relative -top-[1px] ml-1">
                আরো যুক্ত করুন
              </span>
            </Button>
          </>
        )}
      </Form.List>
    </>
  );
}
